import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatRoom, ChatMessage } from '@/services/chatService';

// ─── State ───────────────────────────────────────────────────

interface ChatState {
  room: ChatRoom | null;
  messages: ChatMessage[];
  isOpen: boolean;
  unreadCount: number;
  isAdminTyping: boolean;
  lastUserMessageAt: number | null;
  isLoading: boolean;
  error: string | null;
}

// ─── Actions ─────────────────────────────────────────────────

interface ChatActions {
  // Session
  setRoom: (room: ChatRoom | null) => void;
  clearSession: () => void;

  // Widget toggle
  setIsOpen: (open: boolean) => void;
  toggleWidget: () => void;

  // Messages
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  mergeMessages: (messages: ChatMessage[]) => void;

  // Unread
  incrementUnread: () => void;
  clearUnread: () => void;

  // Typing
  setAdminTyping: (typing: boolean) => void;

  // Activity tracking
  recordUserMessage: () => void;

  // Loading / error
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Full reset
  reset: () => void;
}

// ─── Initial State ───────────────────────────────────────────

const initialState: ChatState = {
  room: null,
  messages: [],
  isOpen: false,
  unreadCount: 0,
  isAdminTyping: false,
  lastUserMessageAt: null,
  isLoading: false,
  error: null,
};

// ─── Store ───────────────────────────────────────────────────

export const useChatStore = create<ChatState & ChatActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setRoom: (room) => set({ room }),

      clearSession: () =>
        set({
          room: null,
          messages: [],
          isAdminTyping: false,
          lastUserMessageAt: null,
          error: null,
        }),

      setIsOpen: (isOpen) => {
        set({ isOpen });
        if (isOpen) set({ unreadCount: 0 });
      },

      toggleWidget: () => {
        const next = !get().isOpen;
        set({ isOpen: next });
        if (next) set({ unreadCount: 0 });
      },

      setMessages: (messages) => set({ messages }),

      addMessage: (message) => {
        const { messages, isOpen } = get();
        // Prevent duplicates
        if (messages.some((m) => m.id === message.id)) return;
        set({ messages: [...messages, message] });
        // Increment unread if collapsed and from admin
        if (!isOpen && message.sender_type === 'admin') {
          set({ unreadCount: get().unreadCount + 1 });
        }
      },

      mergeMessages: (incoming) => {
        const existing = get().messages;
        const existingIds = new Set(existing.map((m) => m.id));
        const newOnes = incoming.filter((m) => !existingIds.has(m.id));
        if (newOnes.length === 0) return;

        // Merge and sort by created_at
        const merged = [...existing, ...newOnes].sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        set({ messages: merged });

        // Increment unread for admin messages when collapsed
        if (!get().isOpen) {
          const adminNew = newOnes.filter((m) => m.sender_type === 'admin');
          if (adminNew.length > 0) {
            set({ unreadCount: get().unreadCount + adminNew.length });
          }
        }
      },

      incrementUnread: () => set({ unreadCount: get().unreadCount + 1 }),
      clearUnread: () => set({ unreadCount: 0 }),

      setAdminTyping: (typing) => set({ isAdminTyping: typing }),

      recordUserMessage: () => set({ lastUserMessageAt: Date.now() }),

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      reset: () => set(initialState),
    }),
    {
      name: 'domainboost-chat',
      partialize: (state) => ({
        room: state.room,
        unreadCount: state.unreadCount,
      }),
    }
  )
);

// ─── Selectors ───────────────────────────────────────────────

export const useChatRoom = () => useChatStore((s) => s.room);
export const useChatIsOpen = () => useChatStore((s) => s.isOpen);
export const useChatMessages = () => useChatStore((s) => s.messages);
export const useChatUnread = () => useChatStore((s) => s.unreadCount);
